import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import ApproveConfirmModal from './ApproveConfirmModal';
import RejectMemberModal from './RejectMemberModal';
import { useAdmin } from '../../contexts/AdminContext';
import { formatRelativeTime } from '../../utils/dateUtils';
import { membershipRequests, getMembershipTypeById } from '../../utils/stubData';

function MembershipRequests() {
  const { approveMember, rejectMember } = useAdmin();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    membershipType: '',
    dateRange: 'all',
  });

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const filteredRequests = membershipRequests
    .filter(request => {
      if (request.status !== 'pending') return false;

      const searchMatch =
        request.firstName.toLowerCase().includes(filters.search.toLowerCase()) ||
        request.lastName.toLowerCase().includes(filters.search.toLowerCase()) ||
        request.email.toLowerCase().includes(filters.search.toLowerCase());

      const typeMatch =
        !filters.membershipType || request.membershipTypeId.toString() === filters.membershipType;

      let dateMatch = true;
      if (filters.dateRange !== 'all') {
        const createdDate = new Date(request.createdAt);
        const now = new Date();
        const daysDiff = (now - createdDate) / (1000 * 60 * 60 * 24);

        switch (filters.dateRange) {
          case '24h':
            dateMatch = daysDiff <= 1;
            break;
          case '7d':
            dateMatch = daysDiff <= 7;
            break;
          case '30d':
            dateMatch = daysDiff <= 30;
            break;
          default:
            dateMatch = true;
        }
      }

      return searchMatch && typeMatch && dateMatch;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const handleApprove = async () => {
    const success = await approveMember(selectedRequest.id);
    if (success) {
      setApproveModalOpen(false);
      setSelectedRequest(null);
    }
  };

  const handleReject = async reason => {
    const success = await rejectMember(selectedRequest.id, reason);
    if (success) {
      setRejectModalOpen(false);
      setSelectedRequest(null);
    }
  };

  const openApproveModal = request => {
    setSelectedRequest(request);
    setApproveModalOpen(true);
  };

  const openRejectModal = request => {
    setSelectedRequest(request);
    setRejectModalOpen(true);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-hdlcc-textDark uppercase">Membership Requests</h2>
        <p className="text-hdlcc-textMuted">{filteredRequests.length} pending applications</p>
      </div>

      {/* Filters */}
      <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="requests-search" className="block text-sm text-hdlcc-textMuted mb-1">
              Search
            </label>
            <input
              id="requests-search"
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by name or email..."
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="requests-membership-type"
              className="block text-sm text-hdlcc-textMuted mb-1"
            >
              Membership Type
            </label>
            <select
              id="requests-membership-type"
              name="membershipType"
              value={filters.membershipType}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="">All Types</option>
              <option value="1">Individual</option>
              <option value="2">Joint</option>
              <option value="4">Youth</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="requests-date-range"
              className="block text-sm text-hdlcc-textMuted mb-1"
            >
              Submitted
            </label>
            <select
              id="requests-date-range"
              name="dateRange"
              value={filters.dateRange}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="all">All Time</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-hdlcc-bgWhite border border-gray-200 rounded-lg overflow-hidden shadow-card">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200">
              <th className="px-4 py-2 text-hdlcc-textDark text-left">Name</th>
              <th className="px-4 py-2 text-hdlcc-textDark text-left">Email</th>
              <th className="px-4 py-2 text-hdlcc-textDark text-left">Type</th>
              <th className="px-4 py-2 text-hdlcc-textDark text-left">Submitted</th>
              <th className="px-4 py-2 text-hdlcc-textDark text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map(request => {
              const membershipType = getMembershipTypeById(request.membershipTypeId);
              return (
                <tr key={request.id} className="border-t border-gray-200 hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/requests/${request.id}`}
                      className="text-hdlcc-accentRed hover:text-red-600"
                    >
                      {request.firstName} {request.lastName}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-hdlcc-textDark">{request.email}</td>
                  <td className="px-4 py-2 capitalize text-hdlcc-textDark">
                    {membershipType?.name}
                  </td>
                  <td className="px-4 py-2 text-hdlcc-textDark">
                    {formatRelativeTime(request.createdAt)}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openApproveModal(request)}
                        className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => openRejectModal(request)}
                        className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-sm transition-colors"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredRequests.length === 0 && (
        <div className="text-center py-12 text-hdlcc-textMuted">
          <p>No pending membership requests.</p>
        </div>
      )}

      <RejectMemberModal
        isOpen={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedRequest(null);
        }}
        onReject={handleReject}
        memberName={
          selectedRequest ? `${selectedRequest.firstName} ${selectedRequest.lastName}` : ''
        }
      />
      <ApproveConfirmModal
        isOpen={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedRequest(null);
        }}
        onConfirm={handleApprove}
        memberName={
          selectedRequest ? `${selectedRequest.firstName} ${selectedRequest.lastName}` : ''
        }
      />
    </div>
  );
}

export default MembershipRequests;
